// Web Audio API Synthesizer & Therapeutic Sound Engine for AbilityOS
// Includes 16+ Clinical Soundscapes, Indian Classical Ragas, Solfeggio Scale, Binaural Waves & Mozart Effect

class WebAudioEngine {
  private ctx: AudioContext | null = null;
  private metronomeTimer: number | null = null;
  private ambientNodes: { oscs: OscillatorNode[]; gain: GainNode; filter?: BiquadFilterNode } | null = null;
  private isAmbientPlaying = false;
  private currentSoundscapeType = 'solfeggio_432';

  private initCtx() {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        this.ctx = new AudioCtxClass();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  /**
   * Play a single musical note (e.g. C4, E4, G4) with a smooth piano/marimba decay envelope
   */
  public playNote(freq: number, waveType: OscillatorType = 'sine', duration = 0.8) {
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = waveType;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      // Envelope: Fast attack, exponential decay
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('AudioContext error:', e);
    }
  }

  /**
   * Play Indian Classical Raga scale note sequences for therapeutic grounding
   */
  public playRagaSequence(ragaName: 'bhupali' | 'yaman' | 'bairagi' | 'ahir_bhairav' | 'malkauns' | 'darbari' = 'bhupali') {
    const ragaFrequencies: Record<string, number[]> = {
      bhupali: [240, 270, 300, 360, 400, 480], // Sa Re Ga Pa Dha Sa'
      yaman: [240, 270, 300, 337.5, 360, 400, 450, 480], // Sa Re Ga Ma' Pa Dha Ni Sa'
      bairagi: [240, 253, 320, 360, 426, 480], // Sa komal-Re Ma Pa komal-Ni Sa'
      ahir_bhairav: [240, 253, 300, 320, 360, 400, 426, 480], // Sa komal-Re Ga Ma Pa Dha komal-Ni
      malkauns: [240, 288, 320, 384, 426, 480], // Sa komal-Ga Ma komal-Dha komal-Ni Sa'
      darbari: [240, 253, 288, 320, 360, 384, 426, 480] // Sa komal-Re komal-Ga Ma Pa komal-Dha komal-Ni Sa'
    };

    const scale = ragaFrequencies[ragaName] || ragaFrequencies.bhupali;
    scale.forEach((freq, idx) => {
      setTimeout(() => {
        this.playNote(freq, 'sine', 1.2);
      }, idx * 450);
    });
  }

  /**
   * Play a single crisp metronome tick
   */
  public playMetronomeTick(isAccent = false) {
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(isAccent ? 1200 : 800, ctx.currentTime);

      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.warn('Metronome tick error:', e);
    }
  }

  /**
   * Start Rhythmic Auditory Stimulation (RAS) Metronome Loop
   */
  public startMetronome(bpm: number, onBeat?: (beatCount: number) => void) {
    this.stopMetronome();
    let beatCount = 0;
    const intervalMs = (60 / bpm) * 1000;

    // Play initial beat
    this.playMetronomeTick(true);
    beatCount++;
    if (onBeat) onBeat(beatCount);

    this.metronomeTimer = window.setInterval(() => {
      beatCount++;
      const isAccent = beatCount % 4 === 1;
      this.playMetronomeTick(isAccent);
      if (onBeat) onBeat(beatCount);
    }, intervalMs);
  }

  public stopMetronome() {
    if (this.metronomeTimer !== null) {
      clearInterval(this.metronomeTimer);
      this.metronomeTimer = null;
    }
  }

  /**
   * Start Comprehensive Therapeutic Music & Soundscape Generator (16+ Channels)
   */
  public startAmbientSoundscape(type: string = 'solfeggio_432') {
    this.stopAmbientSoundscape();
    this.currentSoundscapeType = type;
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.001, ctx.currentTime);
      masterGain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 1.5);
      masterGain.connect(ctx.destination);

      const oscs: OscillatorNode[] = [];

      const soundFrequenciesMap: Record<string, number[]> = {
        solfeggio_174: [174, 180, 285],
        solfeggio_285: [285, 291, 396],
        solfeggio_396: [396, 402, 528],
        solfeggio_417: [417, 423, 528],
        solfeggio_432: [432, 438, 528, 639],
        solfeggio_528: [528, 534, 639, 741],
        solfeggio_639: [639, 645, 741, 852],
        solfeggio_741: [741, 747, 852, 963],
        solfeggio_852: [852, 858, 963],
        solfeggio_963: [963, 969, 1080],
        raga_bhupali: [240, 270, 300, 360, 400, 480],
        raga_yaman: [240, 270, 300, 337.5, 360, 400, 450],
        raga_bairagi: [240, 253, 320, 360, 426],
        raga_ahir_bhairav: [240, 253, 300, 320, 360, 400],
        raga_malkauns: [240, 288, 320, 384, 426],
        raga_darbari: [240, 253, 288, 320, 360, 426],
        mozart_effect: [261.63, 329.63, 392.00, 523.25, 659.25],
        tibetan_bowls: [396, 417, 528, 639, 741, 852, 963]
      };

      if (type === 'alpha_focus') {
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        leftOsc.frequency.setValueAtTime(200, ctx.currentTime);
        rightOsc.frequency.setValueAtTime(210, ctx.currentTime);
        leftOsc.connect(masterGain);
        rightOsc.connect(masterGain);
        leftOsc.start();
        rightOsc.start();
        oscs.push(leftOsc, rightOsc);
      } else if (type === 'theta_calm') {
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        leftOsc.frequency.setValueAtTime(150, ctx.currentTime);
        rightOsc.frequency.setValueAtTime(156, ctx.currentTime);
        leftOsc.connect(masterGain);
        rightOsc.connect(masterGain);
        leftOsc.start();
        rightOsc.start();
        oscs.push(leftOsc, rightOsc);
      } else if (type === 'delta_sleep') {
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        leftOsc.frequency.setValueAtTime(100, ctx.currentTime);
        rightOsc.frequency.setValueAtTime(102, ctx.currentTime);
        leftOsc.connect(masterGain);
        rightOsc.connect(masterGain);
        leftOsc.start();
        rightOsc.start();
        oscs.push(leftOsc, rightOsc);
      } else if (type === 'gamma_memory') {
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        leftOsc.frequency.setValueAtTime(200, ctx.currentTime);
        rightOsc.frequency.setValueAtTime(240, ctx.currentTime);
        leftOsc.connect(masterGain);
        rightOsc.connect(masterGain);
        leftOsc.start();
        rightOsc.start();
        oscs.push(leftOsc, rightOsc);
      } else {
        const freqs = soundFrequenciesMap[type] || soundFrequenciesMap.solfeggio_432;
        freqs.forEach(f => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(f, ctx.currentTime);
          osc.connect(masterGain);
          osc.start();
          oscs.push(osc);
        });
      }

      this.ambientNodes = { oscs, gain: masterGain };
      this.isAmbientPlaying = true;
    } catch (e) {
      console.warn('Ambient Soundscape error:', e);
    }
  }

  public stopAmbientSoundscape() {
    if (this.ambientNodes) {
      try {
        const { oscs, gain } = this.ambientNodes;
        if (this.ctx) {
          gain.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + 0.8);
          setTimeout(() => {
            oscs.forEach(o => o.stop());
          }, 850);
        }
      } catch (e) {
        // ignore
      }
      this.ambientNodes = null;
    }
    this.isAmbientPlaying = false;
  }

  public toggleGlobalBackgroundMusic(): boolean {
    if (this.isAmbientPlaying) {
      this.stopAmbientSoundscape();
      return false;
    } else {
      this.startAmbientSoundscape('solfeggio_432');
      return true;
    }
  }
}

export const audioEngine = new WebAudioEngine();
